/**
 * Node graph — the screen-by-screen structure of every spoke.
 * See schema.ts for the types.
 *
 * `validateNodes()` checks `then:`, `addsSku`, and option `sku:`
 * references, unique option identities within each pick node, and that
 * every terminal commits with exactly one main SKU (no missing, no
 * duplicates along the path). Treat it as the schema-author's safety
 * net.
 */
import { t } from "ttag";

import type { EngineImage, Node, SKU } from "./schema";
import { getOptionId, hub, isAccessory, skuCatalog } from "./schema";

const cooktopRangetopWoImage: EngineImage = {
    src: "tile-images/cooktop-rangetop-wo.webp",
    width: 1236,
    height: 1272,
};
const columnRefrigerationImage: EngineImage = {
    src: "tile-images/column-refrigeration.webp",
    width: 1236,
    height: 1272,
};
const transitionalImage: EngineImage = {
    src: "tile-images/transitional.webp",
    width: 1236,
    height: 1236,
};
const professionalImage: EngineImage = {
    src: "tile-images/professional.webp",
    width: 1236,
    height: 1236,
};
const panelReadyImage: EngineImage = {
    src: "tile-images/panel-ready.webp",
    width: 1236,
    height: 1236,
};
const leftHingeImage: EngineImage = {
    src: "tile-images/left-hinge.webp",
    width: 1236,
    height: 1242,
};
const rightHingeImage: EngineImage = {
    src: "tile-images/right-hinge.webp",
    width: 1236,
    height: 1242,
};

export const nodes: Record<string, Node> = {
    // ============================== COOKING ==============================

    cooking_type: {
        type: "pick",
        prompt: t`Choose a cooking option`,
        pick: "one",
        // Spoke-entry: option subtrees include multi-product chains
        // (Cooktop/Rangetop + Wall Oven), so a single "Starting at"
        // would be misleading. See `suppressPrices` on `PickNode`.
        suppressPrices: true,
        options: [
            { id: "range", label: t`Range`, then: "ranges" },
            {
                id: "cooktop_rangetop_wo",
                label: t`Cooktop/Rangetop + Wall Oven`,
                image: cooktopRangetopWoImage,
                then: "cooking_chain_cooktop_wo",
            },
        ],
    },

    cooking_chain_cooktop_wo: {
        type: "sequence",
        chain: [
            { node: "cooktop_or_rangetop_type", label: t`Cooktop` },
            { node: "wall_oven_type", label: t`Wall Oven` },
        ],
    },

    cooking_type_populated: {
        type: "pick",
        prompt: t`Choose a cooking option`,
        pick: "one",
        // Match the initial-entry picker — spoke-entry, no per-tile prices.
        suppressPrices: true,
        options: [
            { id: "range", label: t`Range`, then: "ranges" },
            {
                id: "cooktop_or_rangetop",
                label: t`Cooktop or Rangetop`,
                then: "cooktop_or_rangetop_type",
            },
            { id: "wall_oven", label: t`Wall Oven`, then: "wall_oven_type" },
        ],
    },

    // --- Range ---
    ranges: {
        type: "pick",
        prompt: t`Select a range size`,
        pick: "one",
        options: [
            { id: "size_48", label: t`48-inch`, then: "range_48_model" },
            { id: "size_36", label: t`36-inch`, then: "range_36_model" },
            { id: "size_30", label: t`30-inch`, then: "range_30_model" },
        ],
    },

    range_48_model: {
        type: "pick",
        prompt: t`Select a range model`,
        pick: "one",
        options: [
            { sku: "SKSDR480SIS" },
            { sku: "SKSDR480GS" },
            { sku: "SKSGR480GS", then: "back_guard_48" },
        ],
    },
    back_guard_48: {
        type: "pick",
        prompt: t`Choose accessories`,
        pick: "any",
        options: [{ sku: "SKSBG4806S" }],
    },

    range_36_model: {
        type: "pick",
        prompt: t`Select a range model`,
        pick: "one",
        options: [
            { sku: "SKSDR360S" },
            { sku: "SKSDR360SS" },
            { sku: "SKSDR360GS" },
            { sku: "SKSDR360SIS" },
            { sku: "SKSGR360S", then: "back_guard_36" },
            { sku: "SKSGR360GS", then: "back_guard_36" },
            { sku: "SKSIR360IS" },
        ],
    },
    back_guard_36: {
        type: "pick",
        prompt: t`Choose accessories`,
        pick: "any",
        options: [{ sku: "SKSBG3606S" }],
    },

    range_30_model: {
        type: "pick",
        prompt: t`Select a range model`,
        pick: "one",
        options: [{ sku: "UPSG3014ST" }],
    },

    // --- Cooktop / Rangetop ---
    cooktop_or_rangetop_type: {
        type: "pick",
        prompt: t`Select a cooktop or rangetop type`,
        pick: "one",
        options: [
            {
                id: "range_top",
                label: t`Rangetop`,
                description: t`Pro-style cooking surface that drops into the countertop, with ultra-high burners for searing and ultra-low burners that simmer to 100°.`,
                then: "rangetop_size",
            },
            {
                id: "induction_cooktop",
                label: t`Induction Cooktop`,
                description: t`Smooth glass cooking surface that heats cookware directly. Flexible zones combine elements to fit different pan sizes.`,
                then: "induction_cooktop_model",
            },
            {
                id: "gas_cooktop",
                label: t`Gas Cooktop`,
                description: t`Sealed gas cooking surface with brass burners. Ultra-high burner for searing, ultra-low burners that simmer to 140°.`,
                then: "gas_cooktop_model",
            },
        ],
    },

    rangetop_size: {
        type: "pick",
        prompt: t`Select a rangetop size`,
        pick: "one",
        options: [
            { id: "rt_size_48", label: t`48-inch`, then: "rangetop_48_model" },
            { id: "rt_size_36", label: t`36-inch`, then: "rangetop_36_model" },
        ],
    },
    rangetop_48_model: {
        type: "pick",
        prompt: t`Select a rangetop model`,
        pick: "one",
        options: [{ sku: "SKSRT480SIS" }, { sku: "SKSRT480GS" }],
    },
    rangetop_36_model: {
        type: "pick",
        prompt: t`Select a rangetop model`,
        pick: "one",
        options: [
            { sku: "SKSRT360SIS" },
            { sku: "SKSRT360GS" },
            { sku: "SKSRT360S" },
        ],
    },

    induction_cooktop_model: {
        type: "pick",
        prompt: t`Select an induction cooktop size`,
        pick: "one",
        options: [{ sku: "SKSIT3601GE" }, { sku: "SKSIT3001GE" }],
    },

    gas_cooktop_model: {
        type: "pick",
        prompt: t`Select a gas cooktop model`,
        pick: "one",
        options: [
            { sku: "SKSGT3654S" },
            { sku: "SKSGT3054S" },
            { sku: "UPCG3654ST" },
            { sku: "UPCG3054ST" },
        ],
    },

    // --- Wall Oven ---
    wall_oven_type: {
        type: "pick",
        prompt: t`Select a wall oven type`,
        pick: "one",
        options: [
            {
                id: "wo_combination",
                label: t`Combination Wall Oven`,
                description: t`A full convection oven stacked with a Speed Cook microwave oven. Modes include steam, sous vide, microwave, and convection.`,
                then: "wall_oven_combi_model",
            },
            {
                id: "wo_double",
                label: t`Double Wall Oven`,
                description: t`Two full-size steam-combi convection ovens stacked in one unit, each with steam and sous vide modes.`,
                then: "wall_oven_double_model",
            },
            {
                id: "wo_single",
                label: t`Single Wall Oven`,
                description: t`A single full-size steam-combi convection oven with steam, sous vide, and convection cooking modes.`,
                then: "wall_oven_single_model",
            },
        ],
    },
    wall_oven_combi_model: {
        type: "pick",
        prompt: t`Select a wall oven model`,
        pick: "one",
        options: [{ sku: "SKSCV3002S" }, { sku: "SKSCV3012MT" }],
    },
    wall_oven_double_model: {
        type: "pick",
        prompt: t`Select a wall oven model`,
        pick: "one",
        options: [{ sku: "SKSDV3002S" }, { sku: "SKSDV3012MT" }],
    },
    wall_oven_single_model: {
        type: "pick",
        prompt: t`Select a wall oven model`,
        pick: "one",
        options: [{ sku: "SKSSV3001S" }, { sku: "SKSSV3011MT" }],
    },

    // =========================== REFRIGERATION ===========================

    refrigeration_type: {
        type: "pick",
        prompt: t`Choose a refrigeration option`,
        pick: "one",
        // Spoke-entry: Column Refrigeration on the initial walk routes
        // into a chain (two columns), so a single "Starting at" would
        // be misleading. See `suppressPrices` on `PickNode`.
        suppressPrices: true,
        options: [
            {
                id: "french_door_sxs",
                label: t`French Door or Side-by-Side`,
                then: "fd_sxs_size",
            },
            {
                id: "column_refrigeration",
                label: t`Column Refrigeration`,
                image: columnRefrigerationImage,
                then: "refrigeration_columns_chain",
            },
            {
                id: "undercounter_refrigeration",
                label: t`Undercounter Refrigeration`,
                then: "undercounter_model",
            },
        ],
    },

    refrigeration_columns_chain: {
        type: "sequence",
        chain: [
            {
                node: "column_pick",
                subcopy: t`1st Column`,
                label: t`1st Column`,
            },
            {
                node: "column_pick",
                subcopy: t`2nd Column`,
                label: t`2nd Column`,
            },
        ],
    },

    refrigeration_type_populated: {
        type: "pick",
        prompt: t`Choose a refrigeration option`,
        pick: "one",
        // Match the initial-entry picker — spoke-entry, no per-tile prices.
        suppressPrices: true,
        options: [
            {
                id: "french_door_sxs",
                label: t`French Door or Side-by-Side`,
                then: "fd_sxs_size",
            },
            {
                id: "column_refrigeration",
                label: t`Column Refrigeration`,
                image: columnRefrigerationImage,
                then: "column_pick",
            },
            {
                id: "undercounter_refrigeration",
                label: t`Undercounter Refrigeration`,
                then: "undercounter_model",
            },
        ],
    },

    // --- French Door / Side-by-Side ---
    fd_sxs_size: {
        type: "pick",
        prompt: t`Select a refrigerator size`,
        pick: "one",
        options: [
            { id: "fd_size_48", label: t`48-inch`, then: "fd_sxs_48_model" },
            { id: "fd_size_42", label: t`42-inch`, then: "fd_sxs_42_model" },
            { id: "fd_size_36", label: t`36-inch`, then: "fd_sxs_36_model" },
        ],
    },
    fd_sxs_48_model: {
        type: "pick",
        prompt: t`Select a refrigerator model`,
        pick: "one",
        options: [
            { sku: "SKSFD4826MT" },
            { sku: "SKSFD4826SP" },
            { sku: "SKSFD4826P" },
        ],
    },
    fd_sxs_42_model: {
        type: "pick",
        prompt: t`Select a refrigerator model`,
        pick: "one",
        options: [{ sku: "SKSSB4202S" }],
    },
    fd_sxs_36_model: {
        type: "pick",
        prompt: t`Select a refrigerator model`,
        pick: "one",
        options: [
            { sku: "SKSFD3623SD" },
            { sku: "SKSFD3604P", then: "fd_sxs_36_panel" },
        ],
    },
    fd_sxs_36_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSPK360FMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSPK360FS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },

    // --- Column pick (single tree, used 1x or 2x via sequence) ---
    column_pick: {
        type: "pick",
        prompt: t`Select a column type`,
        pick: "one",
        options: [
            {
                id: "freezer_column",
                label: t`Freezer Column`,
                then: "column_pick_freezer_size",
            },
            {
                id: "refrigerator_column",
                label: t`Refrigerator Column`,
                then: "column_pick_ref_size",
            },
            {
                id: "wine_column",
                label: t`Wine Column`,
                then: "column_pick_wine_size",
            },
        ],
    },

    // Freezer
    column_pick_freezer_size: {
        type: "pick",
        prompt: t`Select a freezer column size`,
        pick: "one",
        options: [
            {
                id: "fz_size_36",
                label: t`36-inch`,
                then: "column_pick_freezer_36_panel",
            },
            {
                id: "fz_size_30",
                label: t`30-inch`,
                then: "column_pick_freezer_30_panel",
            },
            {
                id: "fz_size_24",
                label: t`24-inch`,
                then: "column_pick_freezer_24_panel",
            },
            {
                id: "fz_size_18",
                label: t`18-inch`,
                then: "column_pick_freezer_18_panel",
            },
        ],
    },
    column_pick_freezer_36_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCF3611P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSPK365CMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSPK365CS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    column_pick_freezer_30_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCF3001P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSPK305CMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSPK305CS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    column_pick_freezer_24_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCF2401P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSPK245CMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSPK245CS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    column_pick_freezer_18_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCF1801P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSPK185CMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSPK185CS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },

    // Refrigerator
    column_pick_ref_size: {
        type: "pick",
        prompt: t`Select Refrigerator Column Size`,
        pick: "one",
        options: [
            {
                id: "ref_size_36",
                label: t`36-inch`,
                then: "column_pick_ref_36_panel",
            },
            {
                id: "ref_size_30",
                label: t`30-inch`,
                then: "column_pick_ref_30_panel",
            },
            {
                id: "ref_size_24",
                label: t`24-inch`,
                then: "column_pick_ref_24_panel",
            },
        ],
    },
    column_pick_ref_36_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCR3611P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSPK365CMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSPK365CS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    column_pick_ref_30_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCR3001P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSPK305CMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSPK305CS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    column_pick_ref_24_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCR2401P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSPK245CMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSPK245CS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },

    // Wine
    column_pick_wine_size: {
        type: "pick",
        prompt: t`Select Wine Column Size`,
        pick: "one",
        options: [
            {
                id: "wine_size_24",
                label: t`24-inch`,
                then: "column_pick_wine_24_swing",
            },
            {
                id: "wine_size_18",
                label: t`18-inch`,
                then: "column_pick_wine_18_swing",
            },
        ],
    },
    // Wine swing pickers are pure routing (size + hinge → which panel screen
    // to show). The column SKU is emitted on the terminal panel pickers,
    // matching the placement convention used by ref/freezer columns.
    column_pick_wine_24_swing: {
        type: "pick",
        prompt: t`Choose Your Door Swing`,
        pick: "one",
        options: [
            {
                id: "left_hinge",
                image: leftHingeImage,
                imageVariant: "full",
                label: t`Left Hinge`,
                description: t`Part of a kit that includes a right handle, a panel, and a toekick.`,
                then: "column_pick_wine_24_left_panel",
            },
            {
                id: "right_hinge",
                image: rightHingeImage,
                imageVariant: "full",
                label: t`Right Hinge`,
                description: t`Part of a kit that includes a left handle, a panel, and a toekick.`,
                then: "column_pick_wine_24_right_panel",
            },
        ],
    },
    column_pick_wine_24_left_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCW241RP",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSWK245LMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSWK245LS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    column_pick_wine_24_right_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCW241RP",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSWK245RMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSWK245RS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    column_pick_wine_18_swing: {
        type: "pick",
        prompt: t`Choose Your Door Swing`,
        pick: "one",
        options: [
            {
                id: "left_hinge",
                image: leftHingeImage,
                imageVariant: "full",
                label: t`Left Hinge`,
                description: t`Part of a kit that includes a right handle, a panel, and a toekick.`,
                then: "column_pick_wine_18_left_panel",
            },
            {
                id: "right_hinge",
                image: rightHingeImage,
                imageVariant: "full",
                label: t`Right Hinge`,
                description: t`Part of a kit that includes a left handle, a panel, and a toekick.`,
                then: "column_pick_wine_18_right_panel",
            },
        ],
    },
    column_pick_wine_18_left_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCW181RP",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSWK185LMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSWK185LS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    column_pick_wine_18_right_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSCW181RP",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSWK185RMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSWK185RS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },

    // --- Undercounter ---
    undercounter_model: {
        type: "pick",
        prompt: t`Select an undercounter model`,
        pick: "one",
        options: [
            {
                id: "drawer",
                label: t`Undercounter Convertible Refrigerator/Freezer Drawers`,
                then: "undercounter_drawer_panel",
            },
            {
                id: "wine",
                label: t`Undercounter Wine Refrigerator`,
                then: "undercounter_wine_panel",
            },
        ],
    },
    undercounter_drawer_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSUD2402P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSUK240DMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSUK240DS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },
    undercounter_wine_panel: {
        type: "pick",
        prompt: t`Choose Panel`,
        pick: "one",
        addsSku: "SKSUW2401P",
        options: [
            {
                id: "transitional",
                image: transitionalImage,
                imageVariant: "full",
                label: t`Transitional Series`,
                description: t`Traditional stainless finish with a bold, tubular handle.`,
                sku: "SKSUK240WMT",
            },
            {
                id: "professional",
                image: professionalImage,
                imageVariant: "full",
                label: t`Professional Series`,
                description: t`Sophisticated satin finish with a sleek, straight handle.`,
                sku: "SKSUK240WS",
            },
            {
                id: "panel_ready",
                image: panelReadyImage,
                imageVariant: "full",
                label: t`Panel-Ready Only`,
                description: t`Use your own custom paneling. Panel and handles sold separately.`,
            },
        ],
    },

    // ============================ DISHWASHER =============================

    dishwasher_type: {
        type: "pick",
        prompt: t`Select a dishwasher model`,
        pick: "one",
        options: [
            { sku: "SKSDW2422S" },
            { sku: "SKSDW2422T" },
            { sku: "SKSDW2422P" },
            { sku: "SKSDW2401S" },
            { sku: "SKSDW2411S" },
            { sku: "SKSDW2411T" },
            { sku: "SKSDW2402P" },
        ],
    },

    // ============================= MICROWAVE =============================

    microwave_type: {
        type: "pick",
        prompt: t`Select a microwave type`,
        pick: "one",
        options: [
            {
                id: "speed_oven",
                label: t`Speed Oven`,
                then: "speed_oven_model",
            },
            { id: "microwave", label: t`Microwave`, then: "microwave_subtype" },
        ],
    },
    speed_oven_model: {
        type: "pick",
        prompt: t`Select a speed oven model`,
        pick: "one",
        options: [{ sku: "SKSLV3001S" }, { sku: "SKSLV3011MT" }],
    },
    microwave_subtype: {
        type: "pick",
        prompt: t`Select a microwave type`,
        pick: "one",
        options: [
            {
                id: "microwave_drawer",
                label: t`Microwave Drawer`,
                sku: "SKSMD2401S",
            },
            {
                id: "countertop_microwave",
                label: t`Microwave`,
                sku: "SKSMC2411S",
                then: "countertop_trim_kit",
            },
            {
                id: "otr_microwave",
                label: t`Over-The-Range Microwave`,
                sku: "UPMC3084ST",
            },
        ],
    },
    countertop_trim_kit: {
        type: "pick",
        prompt: t`Choose Accessories`,
        pick: "any",
        options: [
            {
                sku: "MK2030NST",
                description: t`Allows installation of the countertop microwave into an existing cabinet or over a single built-in oven.`,
            },
        ],
    },

    // ============================ VENTILATION ============================

    ventilation_type: {
        type: "pick",
        prompt: t`Choose a hood type`,
        pick: "one",
        options: [
            {
                id: "chimney_hood",
                label: t`Chimney Hood`,
                then: "chimney_hood_model",
            },
            {
                id: "professional_hood",
                label: t`Professional Hood`,
                then: "professional_hood_size",
            },
            {
                id: "custom_hood_insert",
                label: t`Custom Hood Insert`,
                then: "custom_hood_model",
            },
        ],
    },
    chimney_hood_model: {
        type: "pick",
        prompt: t`Select a hood size`,
        pick: "one",
        options: [{ sku: "SKSCH3601S" }, { sku: "SKSCH3001S" }],
    },
    custom_hood_model: {
        type: "pick",
        prompt: t`Select a hood size`,
        pick: "one",
        options: [{ sku: "SKSHI4801S" }, { sku: "SKSHI3601S" }],
    },
    professional_hood_size: {
        type: "pick",
        prompt: t`Select a professional hood size`,
        pick: "one",
        options: [
            { id: "pro_size_48", label: t`48-inch`, then: "pro_hood_48_duct" },
            { id: "pro_size_36", label: t`36-inch`, then: "pro_hood_36_duct" },
        ],
    },
    pro_hood_48_duct: {
        type: "pick",
        prompt: t`Choose Accessories`,
        pick: "any",
        addsSku: "SKSPH4802S",
        options: [
            {
                sku: "SKSDC480S",
                description: t`This duct cover extends the wall hood height and encloses the space from the top of the wall hood to the ceiling.`,
            },
        ],
    },
    pro_hood_36_duct: {
        type: "pick",
        prompt: t`Choose Accessories`,
        pick: "any",
        addsSku: "SKSPH3602S",
        options: [{ sku: "SKSDC360S" }],
    },
};

// ---------- Validation helpers ----------

const isMainSku = (sku: SKU): boolean => {
    const entry = skuCatalog[sku];
    return !(
        entry.roles.length === 1 && entry.roles[0] === "accessory_eligible"
    );
};

const walkSubFlow = (rootId: string, errors: string[]): void => {
    // Tracks how many main SKUs have been emitted along the current path.
    // The contract is "exactly one main per terminal": `0` at terminal is
    // missing-main, `> 1` anywhere is duplicate-main. Both are reported.
    // Memoize on (nodeId, count) so convergent paths don't re-walk; the
    // count is bounded because we bail on `> 1`, so the state space is
    // {0, 1} in practice.
    const visited = new Map<string, Set<number>>();

    const visit = (nodeId: string, mainCount: number): void => {
        const seen = visited.get(nodeId);
        if (seen?.has(mainCount)) return;
        if (seen) seen.add(mainCount);
        else visited.set(nodeId, new Set([mainCount]));

        const node = nodes[nodeId];
        if (!node) return; // ref-check covers missing
        if (node.type === "sequence") {
            // Walk only the first chain entry from this entry point.
            // Subsequent chain entries are independently validated as
            // their own sub-flow roots.
            const first = node.chain[0];
            visit(typeof first === "string" ? first : first.node, mainCount);
            return;
        }

        const nodeMainCount =
            mainCount +
            (node.addsSku !== undefined && isMainSku(node.addsSku) ? 1 : 0);
        if (nodeMainCount > 1) {
            errors.push(
                `${nodeId}: more than one main SKU emitted along this path (addsSku ${node.addsSku} after upstream main)`,
            );
            return; // stop walking this branch — one error is enough
        }

        // pick:any submit-empty path. SUBMIT_ANY with no options selected
        // commits-or-advances without emitting any option SKU. We walk
        // this path explicitly because the option loop below only models
        // paths where SOME option is selected.
        //
        // Codifies the spec rule that pick:any is an accessory picker:
        // submitting empty must reach a well-formed contribution at every
        // terminal, regardless of how the options were authored.
        if (node.pick === "any") {
            if (!node.then) {
                // Terminal pick:any: submit-empty commits whatever has
                // accumulated upstream plus this node's addsSku.
                if (nodeMainCount === 0) {
                    errors.push(
                        `${nodeId}: pick:any submit-empty path commits without a main SKU`,
                    );
                }
            } else {
                // Non-terminal pick:any: submit-empty advances to
                // node.then with no option SKU applied. The option loop
                // below visits node.then with each option's resulting
                // mainCount; if every option emits a main, the
                // submit-empty mainCount=nodeMainCount path would
                // otherwise be unvisited. Memoization dedupes if any
                // option also reaches node.then with this exact
                // mainCount.
                visit(node.then, nodeMainCount);
            }
        }

        for (const opt of node.options) {
            const optMainCount =
                nodeMainCount +
                (opt.sku !== undefined && isMainSku(opt.sku) ? 1 : 0);
            if (optMainCount > 1) {
                errors.push(
                    `${nodeId} option ${opt.id ?? opt.sku ?? "?"}: more than one main SKU emitted along this path (option sku ${opt.sku} after upstream main)`,
                );
                continue;
            }
            const nextId = opt.then ?? node.then;
            if (!nextId) {
                if (optMainCount === 0) {
                    errors.push(
                        `${nodeId} option ${opt.id ?? opt.sku ?? "?"}: terminal commits without a main SKU`,
                    );
                }
                continue;
            }
            visit(nextId, optMainCount);
        }
    };

    visit(rootId, 0);
};

const checkAllTerminalsCommitMainSku = (errors: string[]): void => {
    // Sub-flow roots: every spoke entry + every chain entry within
    // sequence nodes. Each is validated independently with mainSet
    // starting false.
    const subFlowRoots = new Set<string>();
    for (const spoke of hub.spokes) {
        subFlowRoots.add(spoke.rootNode);
        if (spoke.rootNodePopulated) subFlowRoots.add(spoke.rootNodePopulated);
    }
    for (const node of Object.values(nodes)) {
        if (node.type !== "sequence") continue;
        for (const entry of node.chain) {
            subFlowRoots.add(typeof entry === "string" ? entry : entry.node);
        }
    }

    for (const root of subFlowRoots) {
        // Guard: `walkSubFlow` silently returns when a node id doesn't
        // resolve (intentional for the recursive case, where a typo in
        // `then:` would have already been caught upstream by the per-
        // node ref check). Sub-flow roots have no upstream check —
        // hub `rootNode` / `rootNodePopulated` strings come straight
        // from the spoke config — so a typo there would pass
        // validation silently and only surface at runtime when a
        // shopper enters the spoke. Catch it here instead.
        if (!nodes[root]) {
            errors.push(`sub-flow root "${root}" not found in nodes`);
            continue;
        }
        walkSubFlow(root, errors);
    }
};

/**
 * Validates every `then:` and every SKU reference in the graph. Run
 * from the schema-invariants test so typos in node/SKU ids surface as
 * a single clear list of errors rather than a runtime crash mid-walk.
 */
export const validateNodes = (): string[] => {
    const errors: string[] = [];
    const ids = new Set(Object.keys(nodes));
    const skuIds = new Set(Object.keys(skuCatalog));

    const checkRef = (
        fromId: string,
        ref: string | undefined,
        kind: "then" | "sku" | "addsSku",
    ) => {
        if (!ref) return;
        const valid =
            kind === "sku" || kind === "addsSku"
                ? skuIds.has(ref)
                : ids.has(ref);
        if (!valid) errors.push(`${fromId}: invalid ${kind} -> ${ref}`);
    };

    for (const [id, node] of Object.entries(nodes)) {
        if (node.type === "sequence") {
            for (const entry of node.chain) {
                const target = typeof entry === "string" ? entry : entry.node;
                checkRef(id, target, "then");
                // If a chain entry author opts into the object form, they
                // must supply a `label` — the Skip button uses it for
                // "Skip to [label]" when the shopper is on a prior entry
                // in the chain. Bare strings are still allowed (and fall
                // back to a generic "Skip"); this rule only kicks in once
                // you've upgraded to the object form, so the failure
                // mode is loud, not silent.
                if (typeof entry !== "string" && !entry.label) {
                    errors.push(
                        `${id}: chain entry { node: "${entry.node}" } missing required \`label\``,
                    );
                }
            }
            continue;
        }
        checkRef(id, node.then, "then");
        checkRef(id, node.addsSku, "addsSku");

        if (node.then) {
            errors.push(
                `${id}: node-level \`then\` (every option advancing to ` +
                    `the same next screen) isn't walked by ` +
                    `reachableNonAccessorySkus, so "Starting at $X" ` +
                    `prices would be wrong upstream. Update the walker ` +
                    `before introducing one, or move routing onto options.`,
            );
        }

        // Within a pick node, every option must have a unique identity —
        // that's the key the reducer uses to look up which option was
        // picked. Duplicates would make PICK_ONE / TOGGLE_ANY silently
        // lock to the first match.
        const seen = new Set<string>();
        const dupes = new Set<string>();
        for (const opt of node.options) {
            const optId = getOptionId(opt);
            if (seen.has(optId)) dupes.add(optId);
            seen.add(optId);
            checkRef(id, opt.then, "then");
            checkRef(id, opt.sku, "sku");
        }
        for (const dupe of dupes) {
            errors.push(`${id}: duplicate option identity ${dupe}`);
        }
    }

    // Spec invariant: every contribution commits with a main SKU.
    checkAllTerminalsCommitMainSku(errors);

    for (const sku of Object.keys(skuCatalog) as SKU[]) {
        if (!isAccessory(sku) && !skuCatalog[sku].category) {
            errors.push(`${sku}: non-accessory SKU missing \`category\``);
        }
    }

    return errors;
};
